import { stripMarkdown } from '@littlebird/markdown-utils';
import {
  draftToEditForm,
  isValidRecipient,
  mergeRecipients,
  parseRecipients,
  type EditForm,
  type EmailDraft,
} from '@littlebird/ui-utils';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { SharedMarkdownEditor } from '@/components/ui/editor';
import { Tooltip } from '@/components/ui/Tooltip';
import type {
  DraftLoadState,
  SendState,
} from '@/features/integrations/useSendDraftCard';
import { runFrames } from '@/lib/animate';
import { cn } from '@/lib/utils';

export interface SendDraftCardViewProps {
  draftId: string;
  isAwaitingAutoEdit: boolean;
  isEditing: boolean;
  editForm: EditForm | null;
  setEditForm: Dispatch<SetStateAction<EditForm | null>>;
  showCcBcc: boolean;
  toggleCcBcc: () => void;
  sendState: SendState;
  isUnavailable: boolean;
  isAccountNotFound: boolean;
  loadState: DraftLoadState;
  loadError: { message: string } | null;
  isSaving: boolean;
  draft: EmailDraft | null;
  refetch: () => void;
  editFooterRef: Ref<HTMLSpanElement>;
  IntegrationIcon: ComponentType<{ className?: string }>;
  fromLine: string;
  handleStartEdit: () => void;
  handleCancelEdit: () => void;
  handleSave: () => void;
  handleSend: () => void;
  handleSendFromEdit: () => void;
}

export function SendDraftCardView({
  draftId,
  isAwaitingAutoEdit,
  isEditing,
  editForm,
  setEditForm,
  showCcBcc,
  toggleCcBcc,
  sendState,
  isUnavailable,
  isAccountNotFound,
  loadState,
  loadError,
  isSaving,
  draft,
  refetch,
  editFooterRef,
  IntegrationIcon,
  fromLine,
  handleStartEdit,
  handleCancelEdit,
  handleSave,
  handleSend,
  handleSendFromEdit,
}: SendDraftCardViewProps) {
  const cardRef = useRef<HTMLSpanElement>(null);
  const editScrollRef = useRef<HTMLSpanElement>(null);
  const pendingRef = useRef<Record<string, string>>({});
  const [hasPending, setHasPending] = useState(false);
  const hasCcBccValues = !!editForm?.cc.length || !!editForm?.bcc.length;
  const recipients = useMemo(
    () => (draft ? draftToEditForm(draft).to : []),
    [draft]
  );
  const bodyPreview = useMemo(
    () =>
      draft?.body
        ? stripMarkdown(draft.body)
            .replace(/\n{2,}/g, '\n')
            .trim()
        : '',
    [draft?.body]
  );

  useEffect(() => {
    if (!isEditing) return;
    return runFrames(
      12,
      () => {
        editScrollRef.current?.scrollTo({ top: 0 });
      },
      () => {}
    );
  }, [isEditing]);

  const setPending = useCallback((field: string, value: string) => {
    pendingRef.current[field] = value;
    setHasPending(Object.values(pendingRef.current).some((v) => v.trim()));
  }, []);

  const guardPending = (action: () => void) => () => {
    if (Object.values(pendingRef.current).some((v) => v.trim())) return;
    action();
  };

  if (sendState === 'sent') {
    return (
      <CardShell ref={cardRef} role="status" label="Email sent">
        <CardHeader
          IntegrationIcon={IntegrationIcon}
          title="Email sent"
          subtitle={fromLine}
        >
          <Icons.check className="size-4 text-success-9" aria-hidden="true" />
        </CardHeader>
      </CardShell>
    );
  }

  if (isUnavailable) {
    return (
      <CardShell ref={cardRef} role="status" label="Draft unavailable">
        <CardHeader
          IntegrationIcon={IntegrationIcon}
          title="Draft unavailable"
          subtitle={fromLine}
        >
          <Tooltip label="This draft has been sent or deleted" side="top">
            <span className="flex items-center text-foreground-muted">
              <Icons.info className="size-4" aria-hidden="true" />
            </span>
          </Tooltip>
        </CardHeader>
      </CardShell>
    );
  }

  if (isAccountNotFound) {
    return (
      <CardShell ref={cardRef} role="status" label="Account not connected">
        <CardHeader
          IntegrationIcon={IntegrationIcon}
          title="Account not connected"
          subtitle={fromLine}
        >
          <Tooltip
            label="Reconnect this account in Settings → Integrations"
            side="top"
          >
            <span className="flex items-center text-foreground-muted">
              <Icons.info className="size-4" aria-hidden="true" />
            </span>
          </Tooltip>
        </CardHeader>
      </CardShell>
    );
  }

  if (isAwaitingAutoEdit) {
    return (
      <SendDraftCardSkeleton
        IntegrationIcon={IntegrationIcon}
        fromLine={fromLine}
        fullWidth
      />
    );
  }

  if (isEditing && editForm) {
    return (
      <CardShell
        ref={cardRef}
        label="Edit email draft"
        fullWidth
        className="duration-200 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2"
      >
        <CardHeader
          IntegrationIcon={IntegrationIcon}
          title="Edit draft"
          subtitle={fromLine}
        />
        <span
          ref={editScrollRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          <DraftRow
            label="To"
            action={
              hasCcBccValues ? null : (
                <Button
                  variant="ghost"
                  size="xs"
                  textSize="xs"
                  onClick={toggleCcBcc}
                >
                  {showCcBcc ? 'Hide Cc / Bcc' : 'Cc / Bcc'}
                </Button>
              )
            }
          >
            <RecipientInput
              label="To"
              value={editForm.to}
              onChange={(to) => setEditForm((f) => f && { ...f, to })}
              placeholder="recipient@example.com"
              setPending={setPending}
            />
          </DraftRow>
          {(showCcBcc || hasCcBccValues) && (
            <>
              <DraftRow label="Cc">
                <RecipientInput
                  label="Cc"
                  autoFocus={showCcBcc}
                  value={editForm.cc}
                  onChange={(cc) => setEditForm((f) => f && { ...f, cc })}
                  placeholder="cc@example.com"
                  setPending={setPending}
                />
              </DraftRow>
              <DraftRow label="Bcc">
                <RecipientInput
                  label="Bcc"
                  value={editForm.bcc}
                  onChange={(bcc) => setEditForm((f) => f && { ...f, bcc })}
                  placeholder="bcc@example.com"
                  setPending={setPending}
                />
              </DraftRow>
            </>
          )}
          <DraftRow label="Subject">
            <input
              type="text"
              value={editForm.subject}
              onChange={(e) =>
                setEditForm((f) => f && { ...f, subject: e.target.value })
              }
              aria-label="Subject"
              autoComplete="off"
              placeholder="Subject"
              className="w-full border-0 bg-transparent text-base text-foreground outline-none placeholder:text-foreground-muted"
            />
          </DraftRow>
          <span className="flex max-h-[50vh] min-h-[7.5rem] flex-col overflow-y-auto border-b border-border pb-2 [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base">
            <SharedMarkdownEditor
              key={`email-body-${draftId}`}
              value={editForm.body}
              onChange={(markdown) =>
                setEditForm((f) => f && { ...f, body: markdown })
              }
              autoFocus
              boundaryRef={cardRef}
              placeholder="Email body…"
              ariaLabel="Email body"
              featureSet="email"
              className="flex min-h-0 flex-1 flex-col"
            />
          </span>
        </span>
        <span
          ref={editFooterRef}
          className="flex scroll-mb-12 items-center justify-end gap-2 py-3 pl-5 pr-2.5"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelEdit}
            disabled={isSaving || sendState === 'sending'}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={guardPending(handleSave)}
            isLoading={isSaving && sendState !== 'sending'}
            loadingText="Saving…"
            disabled={sendState === 'sending'}
            tooltip={
              hasPending
                ? 'Finish or clear the recipient you are typing'
                : 'Save without sending'
            }
          >
            Save changes
          </Button>
          <Button
            variant="default"
            size="sm"
            iconSize="xs"
            onClick={guardPending(handleSendFromEdit)}
            isLoading={sendState === 'sending'}
            disabled={isSaving}
            tooltip={
              hasPending
                ? 'Finish or clear the recipient you are typing'
                : 'Save and send this draft'
            }
          >
            <Icons.paperPlane aria-hidden="true" />
            Send
          </Button>
        </span>
      </CardShell>
    );
  }

  return (
    <CardShell ref={cardRef} label="Email draft ready to send">
      <CardHeader
        IntegrationIcon={IntegrationIcon}
        title="Draft ready to send"
        subtitle={fromLine}
      >
        {draft && (
          <Button
            variant="ghost"
            size="sm"
            iconSize="xs"
            onClick={handleStartEdit}
          >
            <Icons.edit aria-hidden="true" />
            Edit
          </Button>
        )}
        <Button
          variant="default"
          size="sm"
          iconSize="xs"
          onClick={handleSend}
          isLoading={sendState === 'sending'}
          disabled={loadState !== 'ready'}
          tooltip={
            loadState === 'ready'
              ? 'Send this draft'
              : 'Load the draft details before sending'
          }
        >
          <Icons.paperPlane aria-hidden="true" />
          Send
        </Button>
      </CardHeader>
      {loadState === 'loading' && <StatusRow>Loading draft details…</StatusRow>}
      {loadState === 'error' && (
        <StatusRow
          action={
            <Button
              variant="link"
              size="xs"
              textSize="xs"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          }
        >
          {loadError?.message ?? 'Failed to load draft details'}
        </StatusRow>
      )}
      {recipients.length > 0 && (
        <DraftRow label="To">
          <span className="line-clamp-2 text-base text-foreground">
            {recipients.join(', ')}
          </span>
        </DraftRow>
      )}
      {draft?.subject && (
        <DraftRow label="Subject">
          <span className="block truncate text-base text-foreground">
            {draft.subject}
          </span>
        </DraftRow>
      )}
      {bodyPreview && (
        <DraftRow label="Body" align="start">
          <span className="line-clamp-3 whitespace-pre-line text-base text-foreground-subtle">
            {bodyPreview}
          </span>
        </DraftRow>
      )}
    </CardShell>
  );
}

/** Card-shaped placeholder for the gap before a draft id exists (e.g. while the
 * share flow's LLM rewrite runs). Real values it can't know yet render as pulses. */
export function SendDraftCardSkeleton({
  IntegrationIcon,
  fromLine,
  fullWidth,
}: {
  IntegrationIcon: ComponentType<{ className?: string }>;
  fromLine: string;
  fullWidth?: boolean;
}) {
  const cardRef = useRef<HTMLSpanElement>(null);
  return (
    <CardShell
      ref={cardRef}
      role="status"
      label="Preparing email"
      fullWidth={fullWidth}
    >
      <CardHeader
        IntegrationIcon={IntegrationIcon}
        title="Preparing email…"
        subtitle={fromLine}
      />
      <DraftRow label="To">
        <SkeletonBar className="w-48" />
      </DraftRow>
      <DraftRow label="Subject">
        <SkeletonBar className="w-64" />
      </DraftRow>
      <span className="flex min-h-[7.5rem] flex-col gap-2.5 py-4 pl-5 pr-2.5">
        <SkeletonBar className="w-full" />
        <SkeletonBar className="w-5/6" />
        <SkeletonBar className="w-2/3" />
      </span>
    </CardShell>
  );
}

// Same look as `ui/skeleton.tsx`, but a span: the card renders in prose contexts
// where a div is invalid nesting.
function SkeletonBar({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'block h-3.5 max-w-full animate-pulse rounded-md bg-surface-active opacity-30',
        className
      )}
    />
  );
}

function CardShell({
  ref,
  role = 'group',
  label,
  fullWidth,
  className,
  children,
}: {
  ref: Ref<HTMLSpanElement>;
  role?: 'group' | 'status';
  label: string;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'not-prose my-3 flex min-h-0 w-full flex-col',
        !fullWidth && 'max-w-lg',
        className
      )}
    >
      <span
        ref={ref}
        role={role}
        aria-label={label}
        className="flex min-h-0 flex-col overflow-hidden rounded-2xl smoothing-default border border-border-strong bg-surface shadow-sm [&>*:last-child]:border-b-0"
      >
        {children}
      </span>
    </span>
  );
}

function CardHeader({
  IntegrationIcon,
  title,
  subtitle,
  children,
}: {
  IntegrationIcon: ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <span className="flex items-center gap-3 border-b border-border py-3 pl-5 pr-2.5">
      <span className="flex size-8 shrink-0 items-center justify-center">
        <IntegrationIcon className="size-7" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-medium text-foreground">
          {title}
        </span>
        {subtitle && (
          <Tooltip label={subtitle} side="top">
            <span className="block truncate text-sm text-foreground-muted">
              {subtitle}
            </span>
          </Tooltip>
        )}
      </span>
      {children && (
        <span className="flex shrink-0 items-center gap-2">{children}</span>
      )}
    </span>
  );
}

function DraftRow({
  label,
  action,
  align = 'center',
  children,
}: {
  label: string;
  action?: ReactNode;
  align?: 'center' | 'start';
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'flex gap-2 border-b border-border py-2.5 pl-5 pr-2.5 focus-within:bg-surface-hover',
        align === 'center' ? 'items-center' : 'items-start'
      )}
    >
      <span
        className={cn(
          'w-[4.5rem] shrink-0 text-base text-foreground-muted',
          align === 'start' && 'pt-0.5'
        )}
      >
        {label}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
      {action && <span className="shrink-0">{action}</span>}
    </span>
  );
}

function StatusRow({
  action,
  children,
}: {
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-3 border-b border-border py-2.5 pl-5 pr-2.5">
      <span className="min-w-0 flex-1 truncate text-base text-foreground-muted">
        {children}
      </span>
      {action && <span className="shrink-0">{action}</span>}
    </span>
  );
}

function RecipientInput({
  label,
  value,
  onChange,
  placeholder,
  setPending,
  autoFocus,
}: {
  label: string;
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  autoFocus?: boolean;
  setPending: (field: string, value: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const inputId = useId();

  useEffect(() => () => setPending(label, ''), [label, setPending]);

  const commit = useCallback(
    (raw: string) => {
      if (!raw.trim()) {
        setDraft('');
        setPending(label, '');
        setError('');
        return;
      }
      const candidates = parseRecipients(raw);
      const invalid = candidates.filter((e) => !isValidRecipient(e));
      if (invalid.length) {
        setError(
          invalid.length === 1
            ? `${invalid[0]} is not a valid email address`
            : `${invalid.length} entries are not valid email addresses`
        );
        return;
      }
      const added = mergeRecipients(value, candidates);
      if (added.length) onChange([...value, ...added]);
      setDraft('');
      setPending(label, '');
      setError('');
    },
    [value, onChange, label, setPending]
  );

  const remove = useCallback(
    (email: string) => onChange(value.filter((v) => v !== email)),
    [value, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      if (!draft.trim()) return;
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <span className="flex max-h-24 flex-wrap items-center gap-1.5 overflow-y-auto">
      {value.map((email) => (
        <span
          key={email}
          className="flex max-w-full items-center gap-1 rounded-md border border-border bg-background-muted py-0.5 pl-1.5 pr-1 text-base text-foreground"
        >
          <span className="truncate">{email}</span>
          <button
            type="button"
            aria-label={`Remove ${email}`}
            onClick={() => remove(email)}
            className="flex size-4 shrink-0 items-center justify-center rounded text-foreground-muted transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strongest"
          >
            <Icons.cancel className="size-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      <label htmlFor={inputId} className="min-w-24 flex-1 cursor-text">
        <input
          id={inputId}
          type="email"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          aria-label={label}
          autoFocus={autoFocus}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setPending(label, e.target.value);
            if (error) setError('');
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => commit(draft)}
          placeholder={value.length === 0 ? placeholder : undefined}
          className="w-full border-0 bg-transparent text-base text-foreground outline-none placeholder:text-foreground-muted"
        />
      </label>
      {error && (
        <span
          id={`${inputId}-error`}
          role="alert"
          className="basis-full text-xs text-danger-11"
        >
          {error}
        </span>
      )}
    </span>
  );
}
